import { PROTOCOL_ERROR_CODE } from '@/core/types/error-codes';
import { request, requestApiOk, requestFormDataApiOk, ApiError } from './client';
import type {
  StoreProduct, ProductCreate, ProductMutation,
  StoreCategory, CategoryCreate, CategoryMutation,
  StoreTag, TagCreate, TagUpdate,
  StoreAttribute, AttributeCreate, AttributeUpdate,
  AttributeOptionCreate, AttributeOptionUpdate,
  BindAttributeRequest, UnbindAttributeRequest, StoreBinding,
  PriceRule, PriceRuleCreate, PriceRuleUpdate,
  LabelTemplate, LabelTemplateCreate, LabelTemplateUpdate,
  SortOrderItem, BatchSortOrderRequest, BulkDeleteRequest,
  StoreInfo, StoreInfoUpdate,
  StoreOpResult,
} from '@/core/types/store';
import type { CouponIssueBatchResponse } from '@/generated/ts-rs/CouponIssueBatchResponse';
import type { TenantImageUploadResponse } from '@/generated/ts-rs/TenantImageUploadResponse';
import type { TenantImageUrlResponse } from '@/generated/ts-rs/TenantImageUrlResponse';

const storePath = (storeId: string, resource: string) =>
  `/api/tenant/stores/${storeId}/${resource}`;

export type { CouponIssueBatchResponse };

function requireNonEmptyResponseField(value: string, field: string, operation: string): string {
  if (value.length > 0) return value;
  throw new ApiError(200, `${operation} returned empty ${field}`, PROTOCOL_ERROR_CODE);
}

function requireImageUploadResponse(data: unknown): TenantImageUploadResponse {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new ApiError(200, 'Image upload returned malformed data payload', PROTOCOL_ERROR_CODE);
  }
  const hash = (data as Record<string, unknown>).hash;
  if (typeof hash !== 'string') {
    throw new ApiError(200, 'Image upload missing required hash', PROTOCOL_ERROR_CODE);
  }
  return { hash: requireNonEmptyResponseField(hash, 'hash', 'Image upload') };
}

function requireImageUrlResponse(data: unknown): TenantImageUrlResponse {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new ApiError(200, 'Image URL lookup returned malformed data payload', PROTOCOL_ERROR_CODE);
  }
  const url = (data as Record<string, unknown>).url;
  if (typeof url !== 'string') {
    throw new ApiError(200, 'Image URL lookup missing required url', PROTOCOL_ERROR_CODE);
  }
  return { url: requireNonEmptyResponseField(url, 'url', 'Image URL lookup') };
}

// ── Products ──
export const listProducts = (token: string, storeId: string) =>
  request<StoreProduct[]>('GET', storePath(storeId, 'products'), undefined, token);

export const createProduct = (token: string, storeId: string, data: ProductCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'products'), data, token);

export const updateProduct = (token: string, storeId: string, id: string, data: ProductMutation) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'products')}/${id}`, data, token);

export const deleteProduct = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'products')}/${id}`, undefined, token);

export const batchUpdateProductSortOrder = (token: string, storeId: string, items: SortOrderItem[]) =>
  request<StoreOpResult>('PATCH', `${storePath(storeId, 'products/sort-order')}`, { items } as BatchSortOrderRequest, token);

export const bulkDeleteProducts = (token: string, storeId: string, ids: string[]) =>
  request<StoreOpResult>('POST', `${storePath(storeId, 'products/bulk-delete')}`, { ids } as BulkDeleteRequest, token);

// ── Categories ──
export const listCategories = (token: string, storeId: string) =>
  request<StoreCategory[]>('GET', storePath(storeId, 'categories'), undefined, token);

export const createCategory = (token: string, storeId: string, data: CategoryCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'categories'), data, token);

export const updateCategory = (token: string, storeId: string, id: string, data: CategoryMutation) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'categories')}/${id}`, data, token);

export const deleteCategory = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'categories')}/${id}`, undefined, token);

export const batchUpdateCategorySortOrder = (token: string, storeId: string, items: SortOrderItem[]) =>
  request<StoreOpResult>('PATCH', `${storePath(storeId, 'categories/sort-order')}`, { items } as BatchSortOrderRequest, token);

// ── Tags ──
export const listTags = (token: string, storeId: string) =>
  request<StoreTag[]>('GET', storePath(storeId, 'tags'), undefined, token);

export const createTag = (token: string, storeId: string, data: TagCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'tags'), data, token);

export const updateTag = (token: string, storeId: string, id: string, data: TagUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'tags')}/${id}`, data, token);

export const deleteTag = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'tags')}/${id}`, undefined, token);

// ── Reverse navigation (Wave E SPEC §6.3) ──
// 列出哪些 PriceRule / PromotionRule / StampActivity 引用该 scope (category / tag / collection / product)。
import type { ReferencingRules } from '@/generated/ts-rs/ReferencingRules';

export type ReferencingScopeKind = 'categories' | 'tags' | 'collections' | 'products';

export const listReferencingRules = (
  token: string,
  storeId: string,
  kind: ReferencingScopeKind,
  id: string,
) =>
  request<ReferencingRules>(
    'GET',
    `${storePath(storeId, kind)}/${id}/referencing-rules`,
    undefined,
    token,
  );

// ── Attributes ──
export const listAttributes = (token: string, storeId: string) =>
  request<StoreAttribute[]>('GET', storePath(storeId, 'attributes'), undefined, token);

export const createAttribute = (token: string, storeId: string, data: AttributeCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'attributes'), data, token);

export const updateAttribute = (token: string, storeId: string, id: string, data: AttributeUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'attributes')}/${id}`, data, token);

export const deleteAttribute = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'attributes')}/${id}`, undefined, token);

// ── Attribute Options (independent CRUD) ──
export const createAttributeOption = (token: string, storeId: string, attributeId: string, data: AttributeOptionCreate) =>
  request<StoreOpResult>('POST', `${storePath(storeId, `attributes/${attributeId}/options`)}`, data, token);

export const updateAttributeOption = (token: string, storeId: string, attributeId: string, optionId: string, data: AttributeOptionUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, `attributes/${attributeId}/options/${optionId}`)}`, data, token);

export const deleteAttributeOption = (token: string, storeId: string, attributeId: string, optionId: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, `attributes/${attributeId}/options/${optionId}`)}`, undefined, token);

export const batchUpdateOptionSortOrder = (token: string, storeId: string, attributeId: string, items: SortOrderItem[]) =>
  request<StoreOpResult>('PATCH', `${storePath(storeId, `attributes/${attributeId}/options/sort-order`)}`, { items } as BatchSortOrderRequest, token);

// ── Attribute Binding ──
export const listBindings = (token: string, storeId: string, ownerType: string, ownerId: string) =>
  request<StoreBinding[]>('GET', `${storePath(storeId, 'attributes/bindings')}?owner_type=${ownerType}&owner_id=${ownerId}`, undefined, token);

export const bindAttribute = (token: string, storeId: string, data: BindAttributeRequest) =>
  request<StoreOpResult>('POST', storePath(storeId, 'attributes/bind'), data, token);

export const unbindAttribute = (token: string, storeId: string, bindingId: string) =>
  request<StoreOpResult>('POST', storePath(storeId, 'attributes/unbind'), { binding_id: bindingId } as UnbindAttributeRequest, token);

// ── Price Rules ──
export const listPriceRules = (token: string, storeId: string) =>
  request<PriceRule[]>('GET', storePath(storeId, 'price-rules'), undefined, token);

export const createPriceRule = (token: string, storeId: string, data: PriceRuleCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'price-rules'), data, token);

export const updatePriceRule = (token: string, storeId: string, id: string, data: PriceRuleUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'price-rules')}/${id}`, data, token);

export const deletePriceRule = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'price-rules')}/${id}`, undefined, token);

// ── Promotion Rules (Spec 1 §3) ──
//
// 立宪 2026-05-28: PromotionRule 是"促销/优惠层"(让利 / Concession),与 PriceRule
// (动态价格调控层)在 fold 顺序与税基归口上分层。Spec 1 scope:
// trigger 仅 MemberGroupActive / target 仅 Scoped / layer 仅 Loyalty|ManualComp /
// tax_treatment 仅 ReducesBase。
import type {
  MenuBundle,
  MenuBundleCreate,
  MenuBundleUpdate,
  PromotionActivity,
  PromotionActivityCreate,
  PromotionActivityUpdate,
  PromotionRule,
  PromotionRuleCreate,
  PromotionRuleUpdate,
} from '@/core/types/store';

export const listPromotionRules = (token: string, storeId: string) =>
  request<PromotionRule[]>('GET', storePath(storeId, 'promotion-rules'), undefined, token);

export const createPromotionRule = (token: string, storeId: string, data: PromotionRuleCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'promotion-rules'), data, token);

export const updatePromotionRule = (token: string, storeId: string, id: string, data: PromotionRuleUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'promotion-rules')}/${id}`, data, token);

export const deletePromotionRule = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'promotion-rules')}/${id}`, undefined, token);

// ── Promotion Activities ──
export const listPromotionActivities = (token: string, storeId: string) =>
  request<PromotionActivity[]>('GET', storePath(storeId, 'promotion-activities'), undefined, token);

export const createPromotionActivity = (token: string, storeId: string, data: PromotionActivityCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'promotion-activities'), data, token);

export const updatePromotionActivity = (
  token: string,
  storeId: string,
  id: string,
  data: PromotionActivityUpdate,
) => request<StoreOpResult>('PUT', `${storePath(storeId, 'promotion-activities')}/${id}`, data, token);

export const deletePromotionActivity = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'promotion-activities')}/${id}`, undefined, token);

// ── Menu Bundles ──
export const listMenuBundles = (token: string, storeId: string) =>
  request<MenuBundle[]>('GET', storePath(storeId, 'menu-bundles'), undefined, token);

export const createMenuBundle = (token: string, storeId: string, data: MenuBundleCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'menu-bundles'), data, token);

export const updateMenuBundle = (token: string, storeId: string, id: string, data: MenuBundleUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'menu-bundles')}/${id}`, data, token);

export const deleteMenuBundle = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'menu-bundles')}/${id}`, undefined, token);

// ── Coupon Definitions / Instances / Batches (Spec 2 §3.1-3.3) ──
//
// Coupon benefit model 尚未落地；create 当前由后端关闭，避免隐藏 PromotionRule bridge。
// CouponInstance 只读为主, 仅支持 revoke。
// IssueBatch 客户端预生成 batch_id, 服务端 PK UNIQUE 幂等。
import type {
  CouponDefinition,
  CouponDefinitionCreate,
  CouponDefinitionUpdate,
  CouponInstance,
  CouponRedemptionStatus,
  CouponIssueBatchRequest,
} from '@/core/types/store';

export const listCouponDefinitions = (token: string, storeId: string) =>
  request<CouponDefinition[]>('GET', storePath(storeId, 'coupon-definitions'), undefined, token);

export const createCouponDefinition = (token: string, storeId: string, data: CouponDefinitionCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'coupon-definitions'), data, token);

export const updateCouponDefinition = (
  token: string,
  storeId: string,
  id: string,
  data: CouponDefinitionUpdate,
) => request<StoreOpResult>('PUT', `${storePath(storeId, 'coupon-definitions')}/${id}`, data, token);

export const deleteCouponDefinition = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'coupon-definitions')}/${id}`, undefined, token);

export interface CouponInstanceFilter {
  status?: CouponRedemptionStatus;
  definitionId?: string;
  issuedTo?: string;
}

export const listCouponInstances = (token: string, storeId: string, filter?: CouponInstanceFilter) => {
  const params = new URLSearchParams();
  if (filter?.status) params.set('status', filter.status);
  if (filter?.definitionId) params.set('definition_id', filter.definitionId);
  if (filter?.issuedTo) params.set('issued_to', filter.issuedTo);
  const qs = params.toString();
  return request<CouponInstance[]>(
    'GET',
    `${storePath(storeId, 'coupon-instances')}${qs ? `?${qs}` : ''}`,
    undefined,
    token,
  );
};

export const revokeCouponInstance = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>(
    'PATCH',
    `${storePath(storeId, 'coupon-instances')}/${id}/revoke`,
    undefined,
    token,
  );

export const issueCouponBatch = (
  token: string,
  storeId: string,
  defId: string,
  payload: CouponIssueBatchRequest,
) =>
  request<CouponIssueBatchResponse>(
    'POST',
    `${storePath(storeId, 'coupon-definitions')}/${defId}/issue-batch`,
    payload,
    token,
  );

// ── KDS Stations ──
import type { KdsStation } from '@/generated/ts-rs/KdsStation';
import type { KdsStationCreate } from '@/generated/ts-rs/KdsStationCreate';
import type { KdsStationUpdate } from '@/generated/ts-rs/KdsStationUpdate';

export const listKdsStations = (token: string, storeId: string) =>
  request<KdsStation[]>('GET', storePath(storeId, 'kds-stations'), undefined, token);

export const createKdsStation = (token: string, storeId: string, data: KdsStationCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'kds-stations'), data, token);

export const updateKdsStation = (token: string, storeId: string, sid: string, data: KdsStationUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'kds-stations')}/${sid}`, data, token);

export const deleteKdsStation = (token: string, storeId: string, sid: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'kds-stations')}/${sid}`, undefined, token);

// ── Product Collections (parity wave §5, 推翻 wave4 Spec A §11) ──
import type { ProductCollection as TProductCollection } from '@/generated/ts-rs/ProductCollection';
import type { CollectionCreate } from '@/generated/ts-rs/CollectionCreate';
import type { CollectionFullReplace } from '@/generated/ts-rs/CollectionFullReplace';

export const listProductCollections = (token: string, storeId: string) =>
  request<TProductCollection[]>('GET', storePath(storeId, 'product-collections'), undefined, token);

export const getProductCollection = (token: string, storeId: string, id: string) =>
  request<TProductCollection>(
    'GET',
    `${storePath(storeId, 'product-collections')}/${id}`,
    undefined,
    token,
  );

export const createProductCollection = (
  token: string,
  storeId: string,
  data: CollectionCreate,
) => request<StoreOpResult>('POST', storePath(storeId, 'product-collections'), data, token);

export const updateProductCollection = (
  token: string,
  storeId: string,
  id: string,
  data: CollectionFullReplace,
) =>
  request<StoreOpResult>(
    'PUT',
    `${storePath(storeId, 'product-collections')}/${id}`,
    data,
    token,
  );

export const deleteProductCollection = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>(
    'DELETE',
    `${storePath(storeId, 'product-collections')}/${id}`,
    undefined,
    token,
  );

// ── Label Templates ──
export const listLabelTemplates = (token: string, storeId: string) =>
  request<LabelTemplate[]>('GET', storePath(storeId, 'label-templates'), undefined, token);

export const createLabelTemplate = (token: string, storeId: string, data: LabelTemplateCreate) =>
  request<StoreOpResult>('POST', storePath(storeId, 'label-templates'), data, token);

export const updateLabelTemplate = (token: string, storeId: string, id: string, data: LabelTemplateUpdate) =>
  request<StoreOpResult>('PUT', `${storePath(storeId, 'label-templates')}/${id}`, data, token);

export const deleteLabelTemplate = (token: string, storeId: string, id: string) =>
  request<StoreOpResult>('DELETE', `${storePath(storeId, 'label-templates')}/${id}`, undefined, token);

// ── Store Info ──
export const getStoreInfo = (token: string, storeId: string, signal?: AbortSignal) =>
  request<StoreInfo>('GET', storePath(storeId, 'store-info'), undefined, token, signal);

export const updateStoreInfo = (token: string, storeId: string, data: StoreInfoUpdate) =>
  request<StoreOpResult>('PUT', storePath(storeId, 'store-info'), data, token);

// ── Image Upload ──

export async function uploadImage(token: string, file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const data = await requestFormDataApiOk<TenantImageUploadResponse>('/api/tenant/images', form, token);
  return requireImageUploadResponse(data).hash;
}

/** Get a presigned S3 URL for an image hash (1h expiry, usable directly as img src) */
export async function getImageUrl(token: string, hash: string): Promise<string> {
  const data = await requestApiOk<TenantImageUrlResponse>('GET', `/api/tenant/images/${hash}`, undefined, token);
  return requireImageUrlResponse(data).url;
}
